import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEntity } from '@backstage/plugin-catalog-react';
import { ToggleButton, ToggleButtonGroup } from '@backstage/ui';
import { CloudResourceEntity, RuleEntity } from '@internal/backstage-plugin-catalog-devops-common';
import { useRulesByEntity } from '@internal/backstage-plugin-devops-react';
import {
  maxSavingOf,
  pillClass,
  resourceNoteOf,
  resourceSeverityOf,
  ruleTitle,
} from './shared';
import styles from './entity-page.module.css';

type StatusFilter = 'Failed' | 'Passed' | 'All';

/**
 * CloudResource page Rules tab, 1:1 with design/cloud-resource-page-mockup.html:
 * every rule evaluated on this resource. Severity and Note are the values
 * THIS resource carries in the rule's spec.resources[] (per-resource, not
 * the rule's worst case); failed rules by default with a status group to
 * show passed ones. Rows navigate to the rule pages.
 */
export function CloudResourceRules() {
  const { entity } = useEntity();
  const resource = entity as CloudResourceEntity;
  const navigate = useNavigate();
  const { rules } = useRulesByEntity(resource);
  const ruleEntities = useMemo(() => rules as RuleEntity[], [rules]);

  const [status, setStatus] = useState<StatusFilter>('Failed');
  const [query, setQuery] = useState('');

  const resourceName =
    resource.metadata.name || resource.metadata.title || '';

  const rows = useMemo(
    () =>
      ruleEntities.map(rule => ({
        rule,
        severity: resourceSeverityOf(rule, resourceName),
        note: resourceNoteOf(rule, resourceName),
        saving: maxSavingOf(rule),
      })),
    [ruleEntities, resourceName],
  );

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows
      .filter(row => {
        if (status !== 'All' && (row.rule.spec.result ?? 'Unknown') !== status) {
          return false;
        }
        if (q) {
          const haystack = [
            ruleTitle(row.rule),
            row.rule.spec.type ?? '',
            row.rule.metadata.description ?? '',
            row.note ?? '',
          ]
            .join(' ')
            .toLowerCase();
          if (!haystack.includes(q)) {
            return false;
          }
        }
        return true;
      })
      .sort(
        (a, b) =>
          (a.rule.spec.result === 'Failed' ? 0 : 1) -
            (b.rule.spec.result === 'Failed' ? 0 : 1) ||
          ruleTitle(a.rule).localeCompare(ruleTitle(b.rule)),
      );
  }, [rows, status, query]);

  return (
    <>
      <div className={styles.toolbar} role="toolbar" aria-label="Rules for this resource">
        <ToggleButtonGroup
          aria-label="Status"
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={new Set([status])}
          onSelectionChange={keys => {
            const next = [...keys][0] as StatusFilter | undefined;
            setStatus(next ?? 'Failed');
          }}
        >
          <ToggleButton key="Failed" id="Failed" size="small">
            Failed
          </ToggleButton>
          <ToggleButton key="Passed" id="Passed" size="small">
            Passed
          </ToggleButton>
          <ToggleButton key="All" id="All" size="small">
            All
          </ToggleButton>
        </ToggleButtonGroup>

        <label className={styles.search}>
          <span aria-hidden="true">⌕</span>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search rules…"
            aria-label="Search rules"
            value={query}
            onChange={event => setQuery(event.target.value)}
          />
        </label>
        <span className={styles.resultNote}>
          {filteredRows.length} of {rows.length} rules shown
        </span>
      </div>

      {rows.length === 0 ? (
        <div className={`${styles.card} ${styles.empty}`}>
          <div className={styles.emptyBig}>✓</div>
          <b>No rules evaluated on this resource</b>
          Rules appear here once a report covers this resource.
        </div>
      ) : (
        <div className={styles.tableCard}>
          <div className={styles.card}>
            <table
              className={styles.table}
              aria-label="Rules affecting this resource"
            >
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Category</th>
                  <th>Severity</th>
                  <th>Status</th>
                  <th>Note</th>
                  <th>Est. saving</th>
                  <th aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(row => (
                  <tr
                    key={row.rule.metadata.name}
                    className={styles.row}
                    onClick={() =>
                      navigate(`/catalog/default/rule/${row.rule.metadata.name}`)
                    }
                  >
                    <td>
                      <span className={styles.ruleName}>
                        {ruleTitle(row.rule)}
                      </span>
                      <br />
                      <span className={styles.ruleCode}>
                        {row.rule.spec.type}
                      </span>
                    </td>
                    <td className={styles.muted}>
                      {row.rule.spec.category ?? '—'}
                    </td>
                    <td>
                      <span
                        className={`${styles.pill} ${pillClass(styles, row.severity)}`}
                      >
                        <span className={styles.pillDot} />
                        {row.severity}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`${styles.pill} ${pillClass(styles, row.rule.spec.result ?? 'Unknown')}`}
                      >
                        <span className={styles.pillDot} />
                        {row.rule.spec.result}
                      </span>
                    </td>
                    <td className={styles.muted}>{row.note ?? '—'}</td>
                    <td>
                      {row.saving ? (
                        <span className={styles.saving}>${row.saving}/mo</span>
                      ) : (
                        <span className={styles.muted}>—</span>
                      )}
                    </td>
                    <td className={styles.cellArr}>↗</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <p className={styles.cardHint} style={{ marginTop: 8 }}>
        Rows link to rule pages. Notes and severities come from{' '}
        <code>spec.resources[]</code> for this resource — the same values the
        rule drawer shows.
      </p>
    </>
  );
}
